import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Injector, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { ActivatedRoute, Router } from '@angular/router';
import { widthExpandAnimation } from '@app/animations/width-expand.animation';
import { SortDirection } from '@app/classes';
import { ConfirmationDialogComponent, DialogComponent, SearchComponent } from '@app/components';
import { AnalyticEvents, FileUploadDirectories } from '@app/enums';
import { DocumentFoldersService } from '@app/modules/documents-v2/services/document-folders.service';
import { DocumentFoldersSidebarComponent } from '@app/modules/documents-v2/standalone-components/document-folders-sidebar/document-folders-sidebar.component';
import { CompanyDocumentDeletionModal } from '@app/modules/documents-v2/views';
import { AnalyticService, AuthService, FileHelperService, NotifyService } from '@app/services';
import { breakPoints } from '@app/styles/theme';
import { getHelpCentreArticle, TranslatableKey } from '@app/types/translatable.type';
import { DriveTypeDisplayOptions } from '@app/types/translatables/drive-name-display.options';
import { DocumentsV2Resolver } from '@documents-v2/documents-v2.resolver';
import { DocumentType, DriveTypeEnum } from '@documents-v2/enums';
import { AllDocumentNodesIndexTable, DocumentMoveNodeService, DocumentNodesIndexTable } from '@documents-v2/services';
import { CompanyDocumentForm } from '@forms/documents/components';
import { CompanyDocument } from '@models/documents/company-document.model';
import { Drive } from '@models/documents/directory/drive.model';
import { Folder } from '@models/documents/directory/folder.model';
import { Node } from '@models/documents/directory/node.model';
import { DocumentTag } from '@models/documents/document-tag.model';
import { SigningTemplate } from '@models/documents/signing-template.model';
import { MenuColumnEvent } from '@table/interfaces/menu-column-event.interface';
import { TableDroppedData } from '@table/types/drag-drop.type';
import { Subscription } from 'rxjs';

type DocumentsDialogType = 'FOLDER_CREATE' | 'FOLDER_RENAME' | 'FOLDER_DELETE' | 'FILE_TYPE_SELECT' | 'FILE_CREATE';

/**
 * As admin, go to Documents, then you are here.
 */
@Component({
    templateUrl: './document-folders-index.template.html',
    styleUrls: ['./document-folders-index.style.scss', '../../../../../../styles/shared-views/documents-index.scss'],
    animations: [widthExpandAnimation(350)],
})
export class DocumentFoldersIndexView implements OnInit, OnDestroy {
    @ViewChild('sidebarComponent', { static: true }) sidebarComponent!: DocumentFoldersSidebarComponent;
    @ViewChild('searchBar') searchBar!: SearchComponent;

    @ViewChild('canDeleteCheckbox') canDeleteCheckbox!: MatCheckbox;

    @ViewChild('companyDocumentDeletionModal') companyDocumentDeletionModal!: CompanyDocumentDeletionModal;

    @ViewChild('newFolderDialog', { static: true }) newFolderDialog!: DialogComponent;
    @ViewChild('publicDataForbiddenDialog', { static: true }) publicDataForbiddenDialog!: ConfirmationDialogComponent;
    @ViewChild('privateViewOnlyDocForbiddenDialog', { static: true })
    privateViewOnlyDocForbiddenDialog!: ConfirmationDialogComponent;

    @ViewChild('moveErrorResult', { static: true }) moveErrorResult!: ConfirmationDialogComponent;

    moveConflicts: string[] = [];
    moveErrorHeader: string = '';
    moveErrorDescription: string = '';

    driveTypeDisplay = DriveTypeDisplayOptions;
    driveTypeEnum = DriveTypeEnum;

    sidebarDrives: Drive[] = [];

    tableParentNode: Node | undefined;
    parentDrive: Drive | undefined;

    isLoading = true;
    isTableLoading = false;
    isSearchTableLoading = false;

    // Used to track if the user has any files or folders, if not, they will be shown a special empty state
    hasAnyFilesOrFolders = true;

    companyDocumentsEditPermission = CompanyDocument.permission.edit;
    currentNode: Node | undefined;

    folderCreateName: string = '';
    folderRename: string = '';
    breadcrumb: { node: Node | null; name: string }[] = [];

    newDocument: CompanyDocument = new CompanyDocument();
    documentType = DocumentType;

    searchQuery: string = '';
    tableBasedOnSearchQuery: AllDocumentNodesIndexTable | undefined;

    documentTags: DocumentTag[] = [];
    selectedTags: DocumentTag[] = [];
    parentSelectedTags: Set<DocumentTag> = new Set<DocumentTag>();

    inRecentDocumentsState = false;

    currentlyOpenDialog: DocumentsDialogType | undefined;

    learnMoreUrl = getHelpCentreArticle('5012032048663');

    protected resolver: DocumentsV2Resolver;

    protected subscription: Subscription | undefined;
    private _inSearchState: boolean = false;
    private _inTagsFilterState: boolean = false;

    constructor(
        private analyticsService: AnalyticService,
        private auth: AuthService,
        private breakpointObserver: BreakpointObserver,
        private documentFoldersService: DocumentFoldersService,
        private documentMoveNodeService: DocumentMoveNodeService,
        private fileHelper: FileHelperService,
        private injector: Injector,
        private notify: NotifyService,
        private route: ActivatedRoute,
        private router: Router,
        public table: DocumentNodesIndexTable
    ) {
        this.resolver = this.injector.get(DocumentsV2Resolver);
        this.table.onLoading.subscribe((isLoading) => {
            this.isTableLoading = isLoading;
        });
    }

    get inSearchState(): boolean {
        return this._inSearchState;
    }

    set inSearchState(value: boolean) {
        if (value && !this.tableBasedOnSearchQuery) {
            this.loadTableBasedOnAllDrives();
        }
        this._inSearchState = value;
    }

    get inTagsFilterState(): boolean {
        return this._inTagsFilterState;
    }

    set inTagsFilterState(value: boolean) {
        if (value && !this.tableBasedOnSearchQuery) {
            this.loadTableBasedOnAllDrives();
        }
        this._inTagsFilterState = value;
    }

    get isMobile(): boolean {
        return !this.breakpointObserver.isMatched(`(min-width: ${breakPoints.lg}px)`);
    }

    get pageContainerClasses(): Array<string> {
        const classes = ['page-container'];
        if (!this.isMobile) {
            classes.push('no-scroll');
            classes.push('h-100');
        }
        return classes;
    }

    async ngOnInit(): Promise<void> {
        this.setSidebarDrives().then(() => {
            this.subscription = this.route.params.subscribe(() => this.resolveNode());
            this.loadTags();
        });
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    onClick(node: Node): void {
        if (node.isDocument() && this.tableParentNode) {
            this.router.navigate(['/documents-v2', 'center', node.sourceId], {
                queryParams: { folderId: this.tableParentNode.id },
            });
            return;
        }
        this.load(node);
    }

    closeDialog(): void {
        this.currentlyOpenDialog = undefined;
    }

    filterByTags(selectedTags: Set<DocumentTag>): void {
        if (!(selectedTags instanceof Set)) {
            return;
        }

        this.inRecentDocumentsState = false;
        this.selectedTags = [...selectedTags];

        if (!this.selectedTags?.length) {
            this.inTagsFilterState = false;
            if (!this.searchQuery) this.inSearchState = false;
            this.tableBasedOnSearchQuery?.setTags(new Set<DocumentTag>());
            return;
        }

        this.inTagsFilterState = true;
        this.tableBasedOnSearchQuery?.setTags(selectedTags);

        this.analyticsService.trackEvent(AnalyticEvents.FiltersDocumentsAndFoldersViaTags);
    }

    removeTag(selectedTag: DocumentTag): void {
        this.parentSelectedTags = new Set(this.selectedTags.filter((tag) => tag !== selectedTag));
        this.filterByTags(this.parentSelectedTags);
    }

    removeAllTags(): void {
        this.parentSelectedTags = new Set([]);
        this.filterByTags(this.parentSelectedTags);
    }

    onSearch(searchQuery: string): void {
        if (!searchQuery) {
            this.inSearchState = false;
        } else {
            this.inSearchState = true;
            this.inRecentDocumentsState = false;
            this.analyticsService.trackEvent(AnalyticEvents.FiltersDocumentsAndFoldersViaSearchBar);
        }

        this.searchQuery = searchQuery;
        if (this.tableBasedOnSearchQuery) {
            this.tableBasedOnSearchQuery.paginator.search = searchQuery;
        }
    }

    onClearSearchFilters(): void {
        this.inSearchState = false;
        this.inRecentDocumentsState = false;
        this.searchQuery = '';
        this.tableBasedOnSearchQuery?.clearFilters();

        if (this.selectedTags?.length) {
            this.removeAllTags();
        }

        if (this.searchBar) {
            this.searchBar.clear();
        }
    }

    onRecentDocuments(): void {
        this.onClearSearchFilters();

        this.inSearchState = true;
        this.inRecentDocumentsState = true;

        if (this.tableBasedOnSearchQuery) {
            this.tableBasedOnSearchQuery.paginator.setSort({ column: 'updatedAt', order: SortDirection.DESC });
            this.tableBasedOnSearchQuery.paginator.search = '%';
        }
    }

    dragStart(event: any): void {
        if (Array.isArray(event.data.data) && event.data.data.length > 1) {
            this.analyticsService.trackEvent(AnalyticEvents.MultiDragEventForDocumentFolders);
        } else {
            this.analyticsService.trackEvent(AnalyticEvents.SingleDragEventForDocumentFolders);
        }
    }

    async onDrop(event: TableDroppedData): Promise<void> {
        const { nodes, nodeIds } = await this.documentMoveNodeService.extractNodes(event.dropped);
        const isMoveValid = await this.documentMoveNodeService.validateMove(
            nodes,
            event.target.id,
            this.privateViewOnlyDocForbiddenDialog,
            this.publicDataForbiddenDialog
        );

        if (isMoveValid) {
            await this.apiCallToMoveNodes(nodeIds, event.target.id);
        }

        if (nodeIds.length === 1) {
            this.analyticsService.trackEvent(AnalyticEvents.SingleDropEventForDocumentFolders);
        }

        if (nodeIds.length > 1) {
            this.analyticsService.trackEvent(AnalyticEvents.MultiDropEventForDocumentFolders);
        }
    }

    /**
     * Resolve is handled within view (not in Routes), this allows us to subscribe to changes in the path and to show
     * the next folder when clicked (Resolvers are not re-resolved on same view).
     */
    resolveNode(): void {
        this.isLoading = true;

        this.resolver
            .resolve(this.route.snapshot, this.router.routerState.snapshot)
            .then((models) => {
                const node = <Node>models.get('node');
                if (node) {
                    this.load(node);
                } else {
                    this.load(this.tableParentNode ?? null);
                }
            })
            .catch((err) => err.message || 'document.unableToReloadTable');
    }

    /**
     * Load table based on given parent node or get default drives
     * and load the table based on default drive such as 'private drive'
     */
    async load(node: Node | null): Promise<void> {
        this.inSearchState = false;
        this.inRecentDocumentsState = false;

        if (node) {
            this.isLoading = true;
            this.tableParentNode = node;
            await this.setParentDrive(this.tableParentNode);

            this.table.loadNode(this.tableParentNode, this.parentDrive);
            this.expandParentNode();
            await this.loadBreadcrumb(node.id);
            this.isLoading = false;
        }
    }

    currentFolderName(): TranslatableKey {
        if (this.tableParentNode?.isDrive()) {
            return this.parentDrive?.type === this.driveTypeEnum.private
                ? this.driveTypeDisplay['private']
                : this.driveTypeDisplay['public'];
        }
        return this.tableParentNode?.name || '';
    }

    newFolder(): void {
        if (!this.tableParentNode) {
            this.notify.error('document.saveError');
            return;
        }
        const folder = new Folder({
            name: this.folderCreateName,
        });
        folder.attach('parent_node_id', this.tableParentNode.id);
        folder
            .create()
            .then(() => {
                this.hasAnyFilesOrFolders = true;
                this.closeDialog();
                this.folderCreateName = '';

                this.table.reload();
                this.reloadSidebar();
            })
            .catch((err) => {
                this.notify.error(err.message);
                this.currentlyOpenDialog = 'FOLDER_CREATE';
            });
    }

    /**
     * Redirect to the bulk upload page using the currently selected folder. If the user's current drive type
     * doesn't match the target drive type, switch to the root folder of the correct drive.
     */
    redirectToBulkUploadPage(driveType: DriveTypeEnum): void {
        let folderId = this.tableParentNode?.id;

        if (this.parentDrive?.type !== driveType) {
            const targetDrive =
                driveType === DriveTypeEnum.private
                    ? this.sidebarDrives.find((drive) => drive.isPrivate())
                    : this.sidebarDrives.find((drive) => drive.isPublic());
            if (targetDrive) {
                folderId = targetDrive.node.id;
            }
        }

        this.router.navigate(['/documents-v2', 'center', 'bulk-upload'], {
            queryParams: { folderId },
        });
    }

    /**
     * Templates can only be selected from the private drive.
     */
    onSelectTemplate(): void {
        if (this.parentDrive?.type !== DriveTypeEnum.private) {
            const targetDrive = this.sidebarDrives.find((drive) => drive.isPrivate());
            if (targetDrive) {
                this.load(targetDrive.node);
            }
        }
        this.currentlyOpenDialog = 'FILE_TYPE_SELECT';
    }

    onSelectDocumentCreationType(type: DocumentType): void {
        this.newDocument = new CompanyDocument({ type });
        this.closeDialog();
        this.currentlyOpenDialog = 'FILE_CREATE';
    }

    getCreateDocumentModalTitle(): TranslatableKey {
        if (this.newDocument?.isDocumentAcknowledgement()) {
            return 'document.viewOnlyDocument';
        }

        if (this.newDocument?.isStandardSigningDocument()) {
            return 'document.signingTemplate';
        }

        if (this.newDocument?.isOfferLetter()) {
            return 'document.offerLetterTemplate';
        }

        return '';
    }

    getCreateDocumentType(): TranslatableKey {
        if (this.newDocument?.isStandardSigningDocument()) {
            return 'document.signingTemplate';
        }

        if (this.newDocument?.isOfferLetter()) {
            return 'document.offerLetter';
        }

        return '';
    }

    async onSaveDocument(form: CompanyDocumentForm): Promise<void> {
        form.submit();
        // If the form is invalid only due to back-end validation, we still want to allow submissions to try again in case it was an intermittent outage
        if (!form.valid || !form.file.value) {
            return;
        }

        this.isLoading = true;

        const isSigningDocument = this.newDocument.isSigningDocument();

        try {
            const file = await this.fileHelper.store(form.file.value, FileUploadDirectories.documents);
            this.hasAnyFilesOrFolders = true;
            this.newDocument.attach('documentTags', [
                ...(await DocumentTag.saveNewTags(form.getNewTags())),
                ...form.getExistingSelectedTags(),
            ]);

            //  Fix TS compiler error: Object is possibly undefined
            // 'throw' of exception caught locally
            if (!this.tableParentNode) {
                throw new Error('document.saveError');
            }

            // Attach current folder node id as parent_node_id to request
            this.newDocument.attach('parent_node_id', this.tableParentNode.id);

            this.newDocument.fileId = file.id;
            const companyDocument = await this.newDocument.save();

            let redirect: Array<string | number> = [companyDocument.id];

            if (isSigningDocument) {
                // TODO: Consider not making the signing template here
                // Instead, the editor page could create the signing template when it's ready
                const signingTemplate = await this.createSigningTemplateForDocument(this.newDocument, form.cosigners);
                redirect = ['/documents-v2/center', companyDocument.id, 'signingTemplate', signingTemplate.id];
            }

            this.router.navigate(redirect);
        } catch (err) {
            form.addExternalError();
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    async menuAction(event: MenuColumnEvent): Promise<void> {
        const { menuItem, data } = event;
        this.currentNode = data as Node;

        switch (menuItem.event) {
            case 'deleteFolder':
                this.currentlyOpenDialog = 'FOLDER_DELETE';
                break;
            case 'deleteDocument':
                this.onDeleteCompanyDocument(this.currentNode.source);
                break;
            case 'download':
                this.onDownload(this.currentNode.source);
                break;
            case 'rename':
                this.currentlyOpenDialog = 'FOLDER_RENAME';
                break;
        }
    }

    async onRenameFolder(): Promise<void> {
        this.isLoading = true;

        try {
            //  Fix TS compiler error: Object is possibly undefined
            // 'throw' of exception caught locally
            if (!this.currentNode) {
                throw new Error();
            }
            const folderToUpdate: Folder = this.currentNode.source;
            folderToUpdate.name = this.folderRename;
            await folderToUpdate.save();
            this.table.reload();
            this.reloadSidebar();
            this.notify.success('document.folderRenamedSuccessfully');
        } catch (e) {
            this.notify.error('document.unableToRenameFolder');
        } finally {
            this.isLoading = false;
            this.closeDialog();
            this.folderRename = '';
        }
    }

    async onDeleteFolder(): Promise<void> {
        this.isLoading = true;

        try {
            //  Fix TS compiler error: Object is possibly undefined
            // 'throw' of exception caught locally
            if (!this.currentNode) {
                throw new Error();
            }
            await this.currentNode.source.delete();
            this.table.reload();
            this.reloadSidebar();
            this.notify.success('document.folderDeletedSuccessfullyNoName');
        } catch (e) {
            this.notify.error('document.unableToDeleteFolder');
        } finally {
            this.isLoading = false;
            this.closeDialog();

            this.canDeleteCheckbox.checked = false;
        }
    }

    protected onDownload(companyDocument: CompanyDocument): void {
        if (companyDocument.fileId && companyDocument.name) {
            this.fileHelper.save(companyDocument.fileId, companyDocument.name, 'pdf');
        }
    }

    protected async onDeleteCompanyDocument(companyDocument: CompanyDocument): Promise<void> {
        const shouldDelete = await this.companyDocumentDeletionModal.open(companyDocument);

        if (!shouldDelete) {
            return;
        }

        this.isLoading = true;
        try {
            await companyDocument.delete();
        } catch (error) {
            this.notify.error(error);
        } finally {
            this.table.reload();
            this.tableBasedOnSearchQuery?.reload();
            this.isLoading = false;
        }
    }

    protected async reloadTableOnSidebarActions(): Promise<void> {
        this.onClearSearchFilters();
        this.table.reload();
        if (this.tableParentNode) {
            this.tableParentNode = await Node.find(this.tableParentNode?.id);
            this.loadBreadcrumb(this.tableParentNode?.id);
        }
    }

    private async loadBreadcrumb(nodeId: number | undefined): Promise<void> {
        if (nodeId === undefined) {
            return;
        }

        const ancestors = await this.documentFoldersService.getNodeAncestors(nodeId);
        this.breadcrumb = ancestors.map((node) => ({ node: node, name: node.name }));
        // remove the drive node to override with display name
        const driveCrumb = this.breadcrumb.shift();

        const driveDisplayName =
            this.parentDrive?.type === this.driveTypeEnum.private
                ? this.driveTypeDisplay['private']
                : this.driveTypeDisplay['public'];
        this.breadcrumb.unshift(
            { name: 'document.allDocuments', node: null },
            { name: driveDisplayName, node: driveCrumb?.node ?? null }
        );
    }

    private async createSigningTemplateForDocument(
        companyDocument: CompanyDocument,
        cosigners: number
    ): Promise<SigningTemplate> {
        const companyDocumentId = companyDocument.id;
        const signingTemplateAttributes: { companyDocumentId: number; cosigners?: number } = { companyDocumentId };

        if (cosigners) {
            signingTemplateAttributes['cosigners'] = cosigners;
        }

        const signingTemplate = new SigningTemplate(signingTemplateAttributes);
        try {
            return signingTemplate.param('companyDocument', companyDocumentId).save();
        } catch (err) {
            companyDocument.delete();
            throw err;
        }
    }

    /**
     * Load the drives this user can access
     */
    private async setSidebarDrives(): Promise<void> {
        try {
            const [drives] = await Drive.all();
            this.sidebarDrives = drives.filter((drive: Drive) => drive.isPrivate() || drive.isPublic());
            this.defaultTableParentNode();

            this.getNumFoldersAndFiles(this.sidebarDrives).then((numFoldersAndFiles) => {
                this.hasAnyFilesOrFolders = numFoldersAndFiles > 0;
            });
        } catch (err) {
            const errorMessage = this.isErrorWithMessage(err) ? err.message : 'document.couldNotFetchSidebarDrives';
            this.notify.error(errorMessage);
        }
    }

    private defaultTableParentNode(): void {
        if (!this.tableParentNode) {
            const drive = this.getDefaultDrive();
            this.tableParentNode = drive?.node;
        }
    }

    private getDefaultDrive(): Drive | null {
        let drive = this.sidebarDrives.find((sidebarDrive: Drive) => sidebarDrive.isPrivate());
        if (drive) {
            return drive;
        }

        drive = this.sidebarDrives.find((sidebarDrive: Drive) => sidebarDrive.isPublic());
        if (drive) {
            return drive;
        }

        return null;
    }

    private async apiCallToMoveNodes(nodesToMove: number[], targetNodeId: number): Promise<void> {
        // Do not allow moving a node to itself
        if (nodesToMove.includes(targetNodeId)) {
            this.notify.error('document.cannotMoveIntoSelf');
            return;
        }

        this.documentFoldersService
            .moveNode(nodesToMove, targetNodeId)
            .then(() => {
                this.notify.success('document.movedItemSuccessfully');
                this.table.reload();
                // always update the sidebar to keep in sync - can optimize this to only update on folder move
                this.reloadSidebar();
            })
            .catch((err: HttpErrorResponse) => {
                const error = this.documentMoveNodeService.parseError(err);

                if (error.moveConflicts.length > 0) {
                    this.moveErrorHeader = error.header;
                    this.moveErrorDescription = error.description;
                    this.moveConflicts = error.moveConflicts;
                    this.moveErrorResult.open().then(() => (this.moveConflicts = []));
                }
            });
    }

    private reloadSidebar(): void {
        this.sidebarComponent.reloadSidebar();
    }

    private expandParentNode(): void {
        if (this.tableParentNode?.parentNodeId) {
            this.sidebarComponent.expandParentNode(this.tableParentNode.parentNodeId);
        }
    }

    private async loadTags(): Promise<void> {
        try {
            const [documentTags] = await DocumentTag.all();
            this.documentTags = documentTags;
        } catch (err) {
            this.notify.error(err);
        }
    }

    private async setParentDrive(tableParentNode: Node): Promise<void> {
        try {
            this.parentDrive = await this.documentFoldersService.getDriveBasedOnNode(tableParentNode);
        } catch (err) {
            const errorMessage = this.isErrorWithMessage(err) ? err.message : 'document.couldNotFetchParentDrive';
            this.notify.error(errorMessage);
        }
    }

    /**
     * This table is used to display contents based on search query
     */
    private loadTableBasedOnAllDrives(): void {
        this.tableBasedOnSearchQuery = new AllDocumentNodesIndexTable(
            this.auth,
            this.sidebarDrives.map((drive) => drive.node.id)
        );
        this.tableBasedOnSearchQuery.onLoading.subscribe((isLoading) => {
            this.isSearchTableLoading = isLoading;
        });
    }

    private async getNumFoldersAndFiles(drives: Drive[]): Promise<number> {
        const [, meta] = await Node.with('source')
            .whereIn(
                'node',
                drives.map((drive) => drive.node.id)
            )
            .limit(1)
            .page(1)
            .get();

        return meta.pagination.total;
    }

    /**
     * Provides flexibility if you're dealing with different types of errors
     */
    private isErrorWithMessage(err: unknown): err is Error {
        return typeof err === 'object' && err !== null && 'message' in err;
    }
}
